import type {
  AnswerRequest,
  AnswerResult,
  ChallengeError,
  ChallengeInfo,
  ChallengeResults,
  CreateChallengeRequest,
  CreateChallengeResult,
  JoinResult,
  NextResult,
  ResultsNotFinished,
} from '@cruz/shared';

/** Client of /api/challenges, built for bad coverage: retries with backoff, no loss. */

export class ApiError extends Error {
  override name = 'ApiError';
  readonly status: number;
  readonly body: ChallengeError | null;
  constructor(status: number, body: ChallengeError | null) {
    super(body?.error ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
  /** No answer from the server (offline, dropped connection) */
  get network(): boolean {
    return this.status === 0;
  }
}

/** Worth retrying: the request may not have arrived, or the server is briefly down. */
const retriable = (e: ApiError) => e.network || [502, 503, 504].includes(e.status);

export interface RequestOptions {
  token?: string | null;
  body?: unknown;
  /** Retries before giving up (network errors and 502–504 only) */
  retries?: number;
  /** Told about each retry, to show "reconectando…" */
  onRetry?: (attempt: number) => void;
  signal?: AbortSignal;
  /** Injected in tests */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  { token, body, retries = 6, onRetry, signal, sleep = defaultSleep }: RequestOptions = {},
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    let error: ApiError;
    try {
      const res = await fetch(path, {
        method,
        headers: {
          ...(body === undefined ? {} : { 'content-type': 'application/json' }),
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      });
      if (res.ok) return (await res.json()) as T;
      let parsed: ChallengeError | null = null;
      try {
        parsed = (await res.json()) as ChallengeError;
      } catch {
        // Not JSON (a proxy error page): keep the status
      }
      error = new ApiError(res.status, parsed);
    } catch (err) {
      if (signal?.aborted) throw err;
      error = new ApiError(0, null);
    }
    if (!retriable(error) || attempt >= retries) throw error;
    onRetry?.(attempt + 1);
    // 0.5 s, 1 s, 2 s, 4 s… up to 8 s, with a little jitter
    await sleep(Math.min(8000, 500 * 2 ** attempt) * (1 + Math.random() * 0.2));
  }
}

const enc = encodeURIComponent;

export const api = {
  create: (body: CreateChallengeRequest) =>
    request<CreateChallengeResult>('POST', '/api/challenges', { body, retries: 2 }),
  info: (code: string, token?: string | null, opts?: RequestOptions) =>
    request<ChallengeInfo>('GET', `/api/challenges/${enc(code)}`, { ...opts, token }),
  join: (code: string, nickname: string) =>
    request<JoinResult>('POST', `/api/challenges/${enc(code)}/participants`, {
      body: { nickname },
      retries: 2,
    }),
  next: (code: string, token: string, opts?: RequestOptions) =>
    request<NextResult>('POST', `/api/challenges/${enc(code)}/next`, { ...opts, token }),
  answer: (code: string, token: string, body: AnswerRequest, opts?: RequestOptions) =>
    request<AnswerResult>('POST', `/api/challenges/${enc(code)}/answers`, {
      ...opts,
      token,
      body,
    }),
  results: (code: string, token: string, opts?: RequestOptions) =>
    request<ChallengeResults | ResultsNotFinished>('GET', `/api/challenges/${enc(code)}/results`, {
      ...opts,
      token,
    }),
};

/** What to tell the player about an error, in Spanish. */
export function errorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Algo ha fallado. Inténtalo de nuevo.';
  if (e.network) return 'No hay conexión. Comprueba la cobertura e inténtalo de nuevo.';
  switch (e.body?.error) {
    case 'not_found':
      return 'Este reto no existe. Revisa el enlace.';
    case 'closed':
      return 'Este reto ya ha terminado.';
    case 'nickname_taken':
      return 'Ese apodo ya está cogido en este reto.';
    case 'not_enough_questions':
      return 'Ahora mismo no podemos preparar las preguntas. Inténtalo más tarde.';
    case 'unauthorized':
      return 'No encontramos tu partida en este móvil.';
    default:
      return e.status === 429
        ? 'Demasiados intentos seguidos. Espera un momento.'
        : 'Algo ha fallado. Inténtalo de nuevo.';
  }
}
